import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { MEMBER_TYPES } from "@/data/pjp";
import {
  ArrowRight, ArrowUpRight, MapPin, Plane, ShieldCheck, ExternalLink,
  User as UserIcon, Radio, Briefcase, Users, Building2, Warehouse,
  Handshake, Wrench, Cog,
} from "lucide-react";

// Per-category accent color + icon so each tile has its own personality
// while sharing the same glass base. Colors tuned to feel aviation-industrial
// (not neon), all readable against dark obsidian.
const CATEGORY_META = {
  "pilots-flight-crew":     { accent: "#4B9CFF", icon: Users,     tag: "PILOTS · CREW" },      // aviation blue
  "charter-operators":      { accent: "#5FE895", icon: Plane,     tag: "OPERATORS" },          // ops green
  "fbo-managers":           { accent: "#F4C443", icon: Building2, tag: "GROUND" },             // ramp yellow
  "aircraft-brokers":       { accent: "#C99BFF", icon: Handshake, tag: "SALES" },              // deal purple
  "parts-manufacturers":    { accent: "#FF9F5A", icon: Warehouse, tag: "SUPPLY CHAIN" },       // industrial orange
  "aviation-sales-service": { accent: "#FF6B9D", icon: Wrench,    tag: "TECHNICAL" },          // tech magenta
};

export function DirectoryHub() {
  const [counts, setCounts] = useState({});
  useEffect(() => { api.get("/directory/stats/counts").then((r) => setCounts(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="overline text-cyan mb-3">WHO BELONGS</div>
      <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-none">Find your place in the network.</h1>
      <p className="mt-5 text-slate-300 max-w-3xl">
        PJP is built for the full spectrum of aviation professionals. Select your member type to view
        the community, browse listed organizations, and learn how to get your own operation listed.
      </p>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {MEMBER_TYPES.map((m) => {
          const meta = CATEGORY_META[m.slug] || { accent: "#4B9CFF", icon: Users, tag: m.kicker };
          const Icon = meta.icon;
          return (
            <Link
              key={m.slug}
              to={`/directory/${m.slug}`}
              data-testid={`dir-tile-${m.slug}`}
              className="group relative isolate"
            >
              {/* Accent-colored glow blob sitting behind the glass so the
                  backdrop-blur renders the color-tinted frosted look. */}
              <div
                aria-hidden="true"
                className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] rounded-full opacity-50 pointer-events-none transition-opacity duration-500 group-hover:opacity-80"
                style={{ background: `radial-gradient(circle, ${meta.accent}55 0%, transparent 70%)`, filter: "blur(70px)" }}
              />

              <div
                className="relative h-full rounded-3xl p-6 overflow-hidden bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border transition-all duration-500 group-hover:-translate-y-1"
                style={{
                  borderColor: `${meta.accent}30`,
                  boxShadow: `0 1px 0 rgba(255,255,255,0.05) inset, 0 24px 40px -24px rgba(0,0,0,0.6), 0 0 60px -30px ${meta.accent}66`,
                }}
              >
                {/* Top-edge highlight strip — the "light on glass" trick */}
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-6 right-6 h-px pointer-events-none"
                  style={{ background: `linear-gradient(90deg, transparent, ${meta.accent}88, transparent)` }}
                />
                {/* Subtle diagonal color wash inside the tile */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none opacity-70 rounded-3xl"
                  style={{ background: `linear-gradient(135deg, ${meta.accent}18 0%, transparent 50%)` }}
                />

                <div className="relative">
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="h-11 w-11 rounded-xl grid place-items-center border"
                      style={{
                        borderColor: `${meta.accent}55`,
                        background: `linear-gradient(140deg, ${meta.accent}25 0%, ${meta.accent}05 100%)`,
                        boxShadow: `0 0 30px -8px ${meta.accent}55`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: meta.accent }} />
                    </div>
                    {counts[m.slug] > 0 && (
                      <span
                        className="mono text-[9px] tracking-widest uppercase px-2 py-1 rounded-full border font-bold"
                        style={{ borderColor: `${meta.accent}55`, background: `${meta.accent}18`, color: meta.accent }}
                      >
                        {counts[m.slug]} LISTED
                      </span>
                    )}
                  </div>

                  <div className="mono text-[10px] tracking-[0.24em] uppercase mb-1" style={{ color: `${meta.accent}CC` }}>
                    {m.kicker}
                  </div>
                  <div className="font-display text-2xl font-semibold tracking-tight text-white mb-2">{m.label}</div>
                  <p className="text-sm text-white/60 mb-6 leading-relaxed">{m.desc}</p>

                  <div
                    className="inline-flex items-center gap-1.5 mono text-[11px] tracking-widest uppercase font-bold transition-all group-hover:gap-2.5"
                    style={{ color: meta.accent }}
                  >
                    MEET THE MEMBERS <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-16 text-center">
        <div className="overline text-cyan mb-3">GET LISTED</div>
        <h2 className="font-display text-3xl font-bold tracking-tight">List your organization in the network.</h2>
        <Link to="/membership" data-testid="dir-cta-membership" className="btn-hud btn-hud-solid mt-6 inline-flex">BECOME A MEMBER <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
    </div>
  );
}

export function DirectoryCategory() {
  const { category } = useParams();
  const meta = MEMBER_TYPES.find((m) => m.slug === category);
  const [orgs, setOrgs] = useState([]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    if (!category) return;
    api.get(`/directory/${category}`).then((r) => {
      // Backward-compat: older callers returned a bare list.
      if (Array.isArray(r.data)) {
        setOrgs(r.data);
        setPeople([]);
      } else {
        setOrgs(r.data.orgs || []);
        setPeople(r.data.people || []);
      }
    }).catch(() => {});
  }, [category]);

  if (!meta) {
    return <div className="max-w-4xl mx-auto p-12 overline">UNKNOWN CATEGORY</div>;
  }

  const total = people.length + orgs.length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <Link to="/directory" data-testid="back-directory" className="overline text-cyan mb-6 inline-block">← ALL DIRECTORIES</Link>
      <div className="overline text-cyan mb-3">// {meta.kicker.toUpperCase()}</div>
      <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-none mb-4">{meta.label}</h1>
      <p className="text-slate-300 max-w-3xl">{meta.desc}</p>

      {/* ==== PEOPLE ==== */}
      {people.length > 0 && (
        <>
          <div className="mt-10 flex items-baseline justify-between">
            <div>
              <div className="overline text-cyan">// MEMBERS</div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-1">People in the network</h2>
            </div>
            <Badge tone="cyan">{people.length} LISTED</Badge>
          </div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {people.map((p) => <PersonCard key={p.id} p={p} />)}
          </div>
        </>
      )}

      {/* ==== ORGS ==== */}
      {orgs.length > 0 && (
        <>
          <div className="mt-14 flex items-baseline justify-between">
            <div>
              <div className="overline text-cyan">// ORGANIZATIONS</div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Companies & agencies</h2>
            </div>
            <Badge tone="cyan">{orgs.length} LISTED</Badge>
          </div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {orgs.map((org) => (
              <Card key={org.id} testId={`org-${org.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 border border-cyan/30 bg-cyan/5 flex items-center justify-center">
                    <Plane className="h-4 w-4 text-cyan" />
                  </div>
                  {org.verified && (
                    <Badge tone="green" pulse><ShieldCheck className="h-3 w-3" /> VERIFIED</Badge>
                  )}
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight">{org.name}</h3>
                {org.tagline && <p className="text-sm text-cyan mt-1">{org.tagline}</p>}
                {org.description && <p className="mt-3 text-sm text-slate-400 line-clamp-3">{org.description}</p>}
                <div className="mt-4 space-y-1.5">
                  {org.location && <div className="overline flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {org.location}</div>}
                  {org.aircraft_fleet?.length > 0 && (
                    <div>
                      <div className="overline mb-1">FLEET</div>
                      <div className="flex flex-wrap gap-1">
                        {org.aircraft_fleet.slice(0, 4).map((a) => (
                          <span key={a} className="font-mono text-[10px] text-slate-300 border border-cyan/15 px-2 py-0.5">{a}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {org.website && (
                    <div className="pt-2">
                      <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`} target="_blank" rel="noreferrer" data-testid={`org-web-${org.id}`} className="overline text-cyan inline-flex items-center gap-1 hover:underline">
                        {org.website.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {total === 0 && (
        <div className="mt-10">
          <Card className="text-center py-16">
            <div className="overline text-slate-500 mb-4">NOTHING LISTED YET</div>
            <p className="text-slate-300 mb-6">Be the first — join PJP and list yourself or your organization in this category.</p>
            <Link to="/membership" data-testid="empty-cta" className="btn-hud btn-hud-solid inline-flex">JOIN & GET LISTED <ArrowRight className="h-3.5 w-3.5" /></Link>
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * Individual member card inside a directory category. Links to /crew/:id.
 */
function PersonCard({ p }) {
  const displayName = p.nickname || `${p.first_name || ""} ${p.last_name || ""}`.trim();
  const initials = ((p.first_name || "?")[0] + (p.last_name || "")[0]).toUpperCase();
  const homeBase = p.location_icao || p.home_base_icao || p.location;
  const isJumpseat = !!p.jumpseat_partner;
  return (
    <Link to={`/crew/${p.id}`} data-testid={`dir-person-${p.id}`} className="group">
      <Card
        className={`h-full !p-5 relative overflow-hidden ${isJumpseat ? "!border-avail/60" : ""}`}
        style={isJumpseat ? { boxShadow: "0 0 30px -8px rgba(46,213,115,0.55), inset 0 0 0 1px rgba(46,213,115,0.15)" } : {}}
      >
        {isJumpseat && (
          <>
            {/* Corner ribbon */}
            <div
              className="absolute -top-3 -right-10 rotate-45 mono text-[9px] font-black tracking-[0.24em] uppercase text-obsidian px-12 py-1 pointer-events-none z-10"
              style={{
                background: "linear-gradient(90deg, #24E37A 0%, #14B85F 100%)",
                boxShadow: "0 4px 14px rgba(46,213,115,0.55)",
              }}
              data-testid={`jumpseat-ribbon-${p.id}`}
            >
              JUMPSEAT · PARTNER
            </div>
            {/* Subtle green gradient wash behind content */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 100% 0%, rgba(46,213,115,0.10), transparent 60%)" }}
            />
          </>
        )}
        <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-12 w-12 rounded-lg overflow-hidden border border-cyan/30 bg-cyan/5 flex items-center justify-center flex-shrink-0">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt={displayName} className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
            ) : (
              <span className="mono text-[11px] text-cyan tracking-widest">{initials}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-semibold tracking-tight text-white truncate group-hover:text-cyan transition-colors">{displayName}</div>
            <div className="overline text-cyan/80 truncate">{p.role_title || (p.role || "").toUpperCase()}</div>
          </div>
          {p.faa_verified && (
            <Badge tone="green"><ShieldCheck className="h-3 w-3" /> FAA</Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-white/70">
          {homeBase && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-cyan/70" /> {homeBase}</span>}
          {p.available_to_work && (
            <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-avail/15 text-avail border border-avail/40 inline-flex items-center gap-1"><Briefcase className="h-2.5 w-2.5" /> AVAIL</span>
          )}
          {p.share_live_location && (
            <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/40 inline-flex items-center gap-1"><Radio className="h-2.5 w-2.5" /> LIVE</span>
          )}
        </div>
        {p.aircraft_types?.length > 0 && (
          <div className="mt-3">
            <div className="overline mb-1 text-white/40">TYPE RATINGS</div>
            <div className="flex flex-wrap gap-1">
              {p.aircraft_types.slice(0, 4).map((a) => (
                <span key={a} className="font-mono text-[10px] text-slate-300 border border-cyan/15 px-2 py-0.5">{a}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 inline-flex items-center gap-1.5 overline text-white/60 group-hover:text-cyan transition-colors">
          VIEW PROFILE <ArrowRight className="h-3 w-3" />
        </div>
        </div>
      </Card>
    </Link>
  );
}
